/**
 * Settings modal — model picker + mock-mode banner.
 *
 * Source references:
 *   - Loveable v1 reference: `src/components/xaes/ClaudeSettingsModal.tsx`
 *     — same role but also exposed an API-key override input (which
 *     the rebuild keeps env-only per ADR 0001 / .env.example).
 *   - `src/stores/useClaudeSettings.ts` — Phase-0 zustand store with
 *     the model enum and persistence layer; we just read/write it.
 */

import { AlertTriangle, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useClaudeSettings } from "@/stores/useClaudeSettings";
import { isClaudeConfigured } from "@/env";
import { cn } from "@/lib/utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type ModelOption = {
  id: "claude-haiku-4-5" | "claude-sonnet-4-6" | "claude-opus-4-7";
  label: string;
  description: string;
};

/** Models the panel exposes. Order is deliberate: cheapest-first so
 *  facilitators don't accidentally rack up Opus charges. */
const MODELS: ModelOption[] = [
  {
    id: "claude-haiku-4-5",
    label: "Haiku 4.5",
    description: "Fastest, lowest cost. Good for routine workshop chat.",
  },
  {
    id: "claude-sonnet-4-6",
    label: "Sonnet 4.6",
    description: "Balanced quality and cost. Default for the rebuild.",
  },
  {
    id: "claude-opus-4-7",
    label: "Opus 4.7",
    description: "Deepest reasoning for synthesis across workshops.",
  },
];

export function ClaudeSettingsModal({ open, onOpenChange }: Props) {
  const model = useClaudeSettings((s) => s.model);
  const setModel = useClaudeSettings((s) => s.setModel);
  const configured = isClaudeConfigured();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-true-blue/30 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm tracking-[0.16em] uppercase">
            XAES Analyst settings
          </DialogTitle>
          <DialogDescription>
            Pick the Claude model used to stream replies. The choice is
            persisted per facilitator.
          </DialogDescription>
        </DialogHeader>

        {!configured && (
          <div className="border-gold/40 bg-gold/10 text-gold flex items-start gap-2 rounded-md border px-2.5 py-2 text-xs">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" strokeWidth={2} aria-hidden />
            <div className="flex flex-col gap-0.5">
              <span className="font-semibold tracking-wide uppercase">Mock mode</span>
              <span className="text-foreground/80">
                <code className="font-mono text-fs-label">VITE_ANTHROPIC_API_KEY</code> is not
                set. Replies are simulated until a key is configured in your
                local <code className="font-mono text-fs-label">.env</code>.
              </span>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <span className="text-muted-foreground font-mono text-fs-label tracking-[0.16em] uppercase">
            Model
          </span>
          <div className="flex flex-col gap-1.5">
            {MODELS.map((m) => {
              const active = m.id === model;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setModel(m.id)}
                  className={cn(
                    "flex items-start gap-2 rounded-md border px-3 py-2 text-left transition-colors",
                    active
                      ? "border-true-blue/60 bg-true-blue/10"
                      : "border-border/40 bg-secondary/40 hover:border-true-blue/40",
                  )}
                >
                  <span
                    className={cn(
                      "mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border",
                      active
                        ? "border-true-blue bg-true-blue text-background"
                        : "border-border bg-background",
                    )}
                    aria-hidden
                  >
                    {active && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
                  </span>
                  <span className="flex flex-col gap-0.5">
                    <span className="text-foreground text-sm font-medium">
                      {m.label}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {m.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-border/40 flex justify-end gap-2 border-t pt-3">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
